import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { vacationsApi, type UserVacation, type VacationPayload } from '@/api/vacations'
import { getErrorMessage } from '@/utils/error'
import { formatDateDMY } from '@/utils/date'
import '@/styles/dashboard.css'

/**
 * Отпуска текущего пользователя. Отпуск учителя останавливает автогенерацию
 * всех его уроков (ученики получают уведомление с датами), отпуск ученика —
 * только его индивидуальных уроков (уведомление учителю).
 */
export function VacationsPage() {
  const { t } = useTranslation()
  const [vacations, setVacations] = useState<UserVacation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<UserVacation | null>(null)
  const [startsOn, setStartsOn] = useState('')
  const [endsOn, setEndsOn] = useState('')
  const [reason, setReason] = useState('')
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuVacation, setMenuVacation] = useState<UserVacation | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<UserVacation | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setVacations(await vacationsApi.list())
    } catch (err) {
      setError(getErrorMessage(err) || t('vacations.loadError', 'Не удалось загрузить отпуска'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  const openCreate = () => {
    setEditing(null)
    setStartsOn('')
    setEndsOn('')
    setReason('')
    setDialogError(null)
    setDialogOpen(true)
  }

  const openEdit = (vacation: UserVacation) => {
    setEditing(vacation)
    setStartsOn(vacation.starts_on)
    setEndsOn(vacation.ends_on)
    setReason(vacation.reason ?? '')
    setDialogError(null)
    setDialogOpen(true)
    setMenuAnchor(null)
  }

  const handleSave = async () => {
    if (!startsOn || !endsOn) {
      setDialogError(t('vacations.datesRequired', 'Укажите даты начала и конца отпуска'))
      return
    }
    if (endsOn < startsOn) {
      setDialogError(t('vacations.datesOrderInvalid', 'Дата конца не может быть раньше даты начала'))
      return
    }
    const payload: VacationPayload = {
      starts_on: startsOn,
      ends_on: endsOn,
      reason: reason.trim() !== '' ? reason.trim() : null,
    }
    setSaving(true)
    setDialogError(null)
    try {
      if (editing) {
        await vacationsApi.update(editing.id, payload)
      } else {
        await vacationsApi.create(payload)
      }
      setDialogOpen(false)
      await load()
    } catch (err) {
      setDialogError(getErrorMessage(err) || t('vacations.saveError', 'Не удалось сохранить отпуск'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await vacationsApi.remove(deleteTarget.id)
      setDeleteTarget(null)
      await load()
    } catch (err) {
      setError(getErrorMessage(err) || t('vacations.deleteError', 'Не удалось удалить отпуск'))
      setDeleteTarget(null)
    }
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('vacations.title', 'Отпуска')}
          </Typography>
          <PageHelpPopover pageKey="vacations" />
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t(
            'vacations.subtitle',
            'На период отпуска уроки не планируются автоматически, а вторая сторона получает уведомление.'
          )}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button size="small" variant="contained" sx={{ height: 40 }} onClick={openCreate}>
            {t('vacations.add', 'Добавить отпуск')}
          </Button>
        </Box>

        {loading ? (
          <Skeleton variant="rounded" height={200} />
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('vacations.period', 'Период')}</TableCell>
                  <TableCell>{t('vacations.reason', 'Причина')}</TableCell>
                  <TableCell align="right">{t('vacations.actions', 'Действия')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vacations.map((vacation) => (
                  <TableRow key={vacation.id} hover>
                    <TableCell>
                      {formatDateDMY(vacation.starts_on)} — {formatDateDMY(vacation.ends_on)}
                    </TableCell>
                    <TableCell>{vacation.reason ?? '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget)
                          setMenuVacation(vacation)
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {vacations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>
                      {t('vacations.empty', 'Отпусков пока нет')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem onClick={() => menuVacation && openEdit(menuVacation)}>
            {t('common.edit', 'Редактировать')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setDeleteTarget(menuVacation)
              setMenuAnchor(null)
            }}
            sx={{ color: 'error.main' }}
          >
            {t('common.delete', 'Удалить')}
          </MenuItem>
        </Menu>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>
            {editing
              ? t('vacations.editTitle', 'Изменить отпуск')
              : t('vacations.addTitle', 'Новый отпуск')}
          </DialogTitle>
          <DialogContent>
            {dialogError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDialogError(null)}>
                {dialogError}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                size="small"
                type="date"
                label={t('vacations.startsOn', 'Начало')}
                value={startsOn}
                onChange={(e) => setStartsOn(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label={t('vacations.endsOn', 'Конец')}
                value={endsOn}
                onChange={(e) => setEndsOn(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                label={t('vacations.reasonOptional', 'Причина (необязательно)')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                inputProps={{ maxLength: 255 }}
              />
              {editing && (
                <Typography variant="caption" color="text.secondary">
                  {t(
                    'vacations.updateHint',
                    'При изменении периода вторая сторона получит новое уведомление.'
                  )}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>{t('common.cancel', 'Отмена')}</Button>
            <Button variant="contained" disabled={saving} onClick={() => void handleSave()}>
              {t('common.save', 'Сохранить')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle>{t('vacations.deleteTitle', 'Удалить отпуск?')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {t(
                'vacations.deleteHint',
                'Пропущенные из-за отпуска уроки будут созданы при ближайшей автогенерации.'
              )}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)}>{t('common.cancel', 'Отмена')}</Button>
            <Button color="error" variant="contained" onClick={() => void handleDelete()}>
              {t('common.delete', 'Удалить')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
